import { useEffect, useState } from 'react'
import { apiClient } from '@/lib/api-client'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Info } from 'lucide-react'

interface SubscriptionChange {
  id: string
  userId: string
  requestedById: string
  previousPlan: string
  newPlan: string
  reason: string
  effectiveAt: string
  createdAt: string
}

interface Invoice {
  id: string
  customerId: string
  customerEmail: string
  amountDue: number
  amountPaid: number
  currency: string
  status: string
  createdAt: string
  hostedUrl: string
}

interface PlatformStats {
  usersByPlan: Record<string, number>
}

export default function AdminBillingPage() {
  const [changes, setChanges] = useState<SubscriptionChange[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [stripeConnected, setStripeConnected] = useState(false)
  const [stats, setStats] = useState<PlatformStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      const [changesResp, invoicesResp, statsResp] = await Promise.all([
        apiClient.listSubscriptionHistory({ limit: 50 }),
        apiClient.listInvoices({ limit: 25 }),
        apiClient.getAdminStats(),
      ])
      if (changesResp.success && changesResp.data) {
        setChanges((changesResp.data as { changes: SubscriptionChange[] }).changes || [])
      }
      if (invoicesResp.success && invoicesResp.data) {
        const data = invoicesResp.data as { invoices: Invoice[]; stripeConnected: boolean }
        setInvoices(data.invoices || [])
        setStripeConnected(data.stripeConnected)
      }
      if (statsResp.success && statsResp.data) {
        setStats(statsResp.data as PlatformStats)
      }
      setLoading(false)
    }
    load()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    )
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'usd' }).format(amount / 100)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Billing Management</h1>
        <p className="text-sm text-muted-foreground">Subscriptions, plan changes, and invoices</p>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="changes">Subscription Changes</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <Card className="app-surface">
            <CardHeader>
              <CardTitle className="text-base">Users by Plan</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats?.usersByPlan && Object.entries(stats.usersByPlan).map(([plan, count]) => (
                  <div key={plan} className="flex items-center justify-between">
                    <Badge variant="outline" className="capitalize">{plan}</Badge>
                    <span className="text-sm font-medium tabular-nums">{count}</span>
                  </div>
                ))}
                {(!stats?.usersByPlan || Object.keys(stats.usersByPlan).length === 0) && (
                  <p className="text-sm text-muted-foreground">No data</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="changes" className="mt-4">
          <Card className="app-surface">
            <CardContent className="pt-6">
              {changes.length > 0 ? (
                <div className="overflow-x-auto"><table className="w-full text-sm min-w-[500px]">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="pb-2">Date</th>
                      <th className="pb-2">User</th>
                      <th className="pb-2">From</th>
                      <th className="pb-2">To</th>
                      <th className="pb-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changes.map((change) => (
                      <tr key={change.id} className="border-b last:border-0">
                        <td className="py-2.5 tabular-nums">{new Date(change.createdAt).toLocaleDateString()}</td>
                        <td className="py-2.5 font-mono text-xs">{change.userId.slice(0, 8)}...</td>
                        <td className="py-2.5"><Badge variant="outline" className="text-[10px] capitalize">{change.previousPlan}</Badge></td>
                        <td className="py-2.5"><Badge variant="outline" className="text-[10px] capitalize">{change.newPlan}</Badge></td>
                        <td className="py-2.5 text-muted-foreground">{change.reason || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              ) : (
                <p className="text-sm text-muted-foreground">No subscription changes</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          {!stripeConnected && (
            <Alert className="mb-4">
              <Info className="h-4 w-4" />
              <AlertDescription>
                Stripe integration not configured. Set <code className="text-xs">STRIPE_SECRET_KEY</code> in api-gateway to view invoices.
              </AlertDescription>
            </Alert>
          )}
          <Card className="app-surface">
            <CardContent className="pt-6">
              {invoices.length > 0 ? (
                <div className="overflow-x-auto"><table className="w-full text-sm min-w-[500px]">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="pb-2">Date</th>
                      <th className="pb-2">Customer</th>
                      <th className="pb-2">Amount</th>
                      <th className="pb-2">Status</th>
                      <th className="pb-2">Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id} className="border-b last:border-0">
                        <td className="py-2.5 tabular-nums">{new Date(inv.createdAt).toLocaleDateString()}</td>
                        <td className="py-2.5">{inv.customerEmail || inv.customerId}</td>
                        <td className="py-2.5 tabular-nums">{formatCurrency(inv.amountDue, inv.currency)}</td>
                        <td className="py-2.5">
                          <Badge variant={inv.status === 'paid' ? 'default' : 'outline'} className="text-[10px] capitalize">
                            {inv.status}
                          </Badge>
                        </td>
                        <td className="py-2.5">
                          {inv.hostedUrl && (
                            <a href={inv.hostedUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                              View
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {stripeConnected ? 'No invoices found' : 'Connect Stripe to view invoices'}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
